import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, api, API_BASE } from '../lib/api';
import { AppShell } from '../components/Layout';
import { Banner, Field, Loading } from '../components/ui';
import { FieldEditor, fromApiFields, toApiFields } from '../components/FieldEditor';
import type { EditorField } from '../components/FieldEditor';
import { useToast } from '../state/toast';
import { useSession } from '../state/session';

/**
 * Card-free preparation workspace.
 *
 * Visitors upload, place fields and preview with no account and no card. The
 * work is preserved through account creation and payment setup.
 */
export function Prepare() {
  const navigate = useNavigate();
  const { notify } = useToast();
  const { user } = useSession();
  const [workspace, setWorkspace] = useState<any>(null);
  const [doc, setDoc] = useState<any>(null);
  const [fields, setFields] = useState<EditorField[]>([]);
  const [detection, setDetection] = useState<any>(null);
  const [signatories, setSignatories] = useState<any[]>([]);
  const [activeSignatory, setActiveSignatory] = useState<string | null>(null);
  const [askingEmails, setAskingEmails] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const saveTimer = useRef<number | null>(null);

  useEffect(() => {
    (async () => {
      try {
        await api.post('/guest/workspace');
        const data = await api.get('/guest/workspace');
        setWorkspace(data);
        const existing = data.documents?.[0];
        if (existing) {
          setDoc(existing);
          setFields(fromApiFields(data.fields?.[existing.id] || []));
        }
      } catch {
        setError('The preparation workspace could not be opened. Reload to try again.');
      }
    })();
  }, []);

  /**
   * Ask for field suggestions, and place them.
   *
   * A visitor has nothing invested yet, so an empty field editor is the point
   * at which most of them leave. Placing the fields for them is the whole
   * demonstration. Everything is still editable — and a failure here is
   * silent, because somebody trying the product out should not be shown an
   * error about a feature they did not ask for.
   */
  const suggestFields = useCallback(async (document: any) => {
    if (!document) return;
    setDetecting(true);
    try {
      const result = await api.post(`/guest/documents/${document.id}/detect`);
      const found = result.detection;
      setDetection(found);
      // The signatory key doubles as the assignee id here. A guest has no
      // recipients yet — there is no account to hang them on — so the person
      // read off the document is the identity a field is tied to until one
      // exists.
      const people = (found?.signatories || []).map((person: any, index: number) => ({
        key: person.key,
        name: person.name || `Signer ${index + 1}`,
        title: person.title || '',
        organization: person.organization || '',
        email: '',
      }));
      setSignatories(people);

      if (found?.fields?.length) {
        setFields(found.fields
          .filter((f: any) => f.kind !== 'radio')
          .map((f: any, index: number) => ({
            localId: `d${index}`,
            kind: f.kind,
            label: f.label || f.kind,
            page: f.page,
            x: f.x, y: f.y, width: f.width, height: f.height,
            required: f.required !== false,
            // Not a recipient id: nothing has been created yet. It is the
            // detected signatory, carried through to the account on sign-up.
            recipient_id: f.signatory_key || people[0]?.key || null,
            role_key: f.signatory_key || null,
          })));
      }
    } catch {
      setDetection(null);
    } finally {
      setDetecting(false);
    }
  }, []);

  const upload = useCallback(async (file: File) => {
    setBusy(true); setError(null);
    try {
      const body = new FormData();
      body.append('file', file);
      const uploaded = await api.post('/guest/documents', body);
      setDoc(uploaded);
      setFields([]);
      setDetection(null);
      notify('Document uploaded and validated.', 'ok');
      void suggestFields(uploaded);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'That file could not be uploaded.');
    } finally {
      setBusy(false);
    }
  }, [notify, suggestFields]);

  // Autosave field positions so nothing is lost on refresh or sign-up.
  useEffect(() => {
    if (!doc) return;
    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      api.put(`/guest/documents/${doc.id}/fields`, { fields: toApiFields(fields) })
        .catch(() => undefined);
    }, 700);
    return () => { if (saveTimer.current) window.clearTimeout(saveTimer.current); };
  }, [fields, doc]);

  const continueToSend = async () => {
    if (!doc) return;
    // Ask for the addresses before leaving. The document named these people;
    // all that is missing is where to send it, and asking here means it is
    // typed once rather than again after signing up.
    if (signatories.length && !askingEmails
        && signatories.some((person) => !person.email.trim())) {
      setAskingEmails(true);
      return;
    }
    setBusy(true);
    try {
      await api.put(`/guest/documents/${doc.id}/fields`, { fields: toApiFields(fields) });
      if (signatories.length) {
        await api.put(`/guest/documents/${doc.id}/signatories`, {
          signatories: signatories.map((person) => ({
            key: person.key, email: person.email.trim(),
          })),
        }).catch(() => undefined);   // addresses are a convenience, not a gate
      }
      await api.post(`/guest/documents/${doc.id}/preview`);
      navigate(user ? '/app/new' : '/register?next=send');
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Something went wrong.');
      setBusy(false);
    }
  };

  const hasSignature = fields.some((f) => f.kind === 'signature' || f.kind === 'initials');

  return (
    <AppShell
      nav={[{ to: '/', label: 'Home' }, { to: '/pricing', label: 'Pricing' }]}
      actions={user
        ? <Link className="btn btn--small" to="/app">Dashboard</Link>
        : <Link className="btn btn--small btn--ghost" to="/login">Sign in</Link>}
    >
      <div className="shell section stack">
        <header className="stack-s">
          <h1>Prepare your agreement</h1>
          <p className="muted" style={{ maxWidth: '64ch' }}>
            Upload a PDF and place the fields your signer needs to complete. No
            account and no card are required for this step — you keep this work
            when you sign up.
          </p>
        </header>

        {error && <Banner tone="danger" title="That did not work">{error}</Banner>}

        {!workspace ? <Loading label="Opening your workspace…" /> : !doc ? (
          <div className="card stack">
            <h2 style={{ margin: 0 }}>Upload a PDF</h2>
            <p className="muted small" style={{ margin: 0 }}>
              Up to {workspace.limits.max_upload_mb} MB and {workspace.limits.max_pages} pages.
              Encrypted, malformed, already-signed PDFs and files containing active
              content are rejected with the reason.
            </p>
            <input
              ref={fileInput}
              type="file"
              accept="application/pdf,.pdf"
              className="input"
              style={{ paddingTop: 10 }}
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void upload(file);
              }}
            />
            {busy && <Loading label="Validating your document…" />}
            <p className="small muted" style={{ margin: 0 }}>{workspace.retention_note}</p>
          </div>
        ) : (
          <>
            <div className="card card--flat row row--between">
              <div style={{ minWidth: 0 }}>
                <strong className="truncate" style={{ display: 'block' }}>{doc.name}</strong>
                <span className="small muted">
                  {doc.page_count} page{doc.page_count === 1 ? '' : 's'} · PDF {doc.validation.pdf_version}
                  {' '}· {(doc.validation.byte_length / 1024).toFixed(0)} KB
                </span>
              </div>
              <button type="button" className="btn btn--small"
                      onClick={() => { setDoc(null); setFields([]); setDetection(null); setSignatories([]); }}>
                Replace
              </button>
            </div>

            {detecting && (
              <Banner tone="info" title="Reading your document">
                Looking for the signature block so the fields can be placed
                for you.
              </Banner>
            )}
            {!detecting && detection?.fields?.length > 0 && (
              <Banner tone="ok" title={
                detection.fields.length === 1
                  ? 'Placed one field for you'
                  : `Placed ${detection.fields.length} fields for you`}>
                Read from the signature block in your document. Drag, resize
                or delete any of them — nothing is fixed.
                {detection.signatories?.length > 1
                  ? ` We also found ${detection.signatories.length} signatories;`
                    + ' you will add their email addresses after signing up.'
                  : ''}
              </Banner>
            )}
            {!detecting && detection && !detection.fields?.length && (
              <Banner tone="info" title="No signature block found">
                {detection.reason || 'Place the fields where they belong — '
                  + 'drag one from the toolbar onto the page.'}
              </Banner>
            )}

            <FieldEditor
              url={`${API_BASE}/guest/documents/${doc.id}/file`}
              pageSizes={doc.page_sizes}
              fields={fields}
              onChange={setFields}
              assignees={signatories.map((person) => ({
                id: person.key, name: person.name,
              }))}
              activeAssignee={activeSignatory}
              onActiveAssignee={setActiveSignatory}
            />

            <div className="actionbar">
              <span className="small muted spacer">
                {fields.length} field{fields.length === 1 ? '' : 's'} placed
                {!hasSignature && fields.length > 0 && ' · add a signature field'}
              </span>
              <button
                type="button"
                className="btn btn--primary"
                disabled={!hasSignature || busy}
                onClick={() => void continueToSend()}
              >
                {busy ? 'Saving…' : 'Continue to send'}
              </button>
            </div>

            {askingEmails && (
              <div className="card stack">
                <h2 style={{ margin: 0, fontSize: '1.05rem' }}>
                  Where should this go?
                </h2>
                <p className="card__sub" style={{ margin: 0 }}>
                  We read these names from the document. Add an address for
                  each — you can change them before anything is sent.
                </p>
                {signatories.map((person, index) => (
                  <Field key={person.key}
                         label={person.name}
                         id={`sig-${person.key}`}
                         hint={[person.title, person.organization]
                           .filter(Boolean).join(' · ') || undefined}>
                    <input id={`sig-${person.key}`} className="input" type="email"
                           inputMode="email" autoFocus={index === 0}
                           placeholder="name@company.com"
                           value={person.email}
                           onChange={(event) => setSignatories(signatories.map((p) =>
                             p.key === person.key
                               ? { ...p, email: event.target.value } : p))} />
                  </Field>
                ))}
                <div className="row row--between">
                  <button type="button" className="btn btn--ghost btn--small"
                          onClick={() => setAskingEmails(false)}>
                    Back to the fields
                  </button>
                  <button type="button" className="btn btn--primary"
                          disabled={busy}
                          onClick={() => void continueToSend()}>
                    {busy ? 'Saving…' : 'Continue'}
                  </button>
                </div>
                <p className="small muted" style={{ margin: 0 }}>
                  You can continue without them and add addresses later.
                </p>
              </div>
            )}

            <Banner tone="info" title="What happens next">
              You will create an account, then activate the three-day sending trial
              with a card. Your document and field positions carry over. Nothing is
              charged until the trial ends, and you can cancel before then.
            </Banner>
          </>
        )}
      </div>
    </AppShell>
  );
}
